import React from "react";
import { youtube_v3 } from "googleapis";
import moment from "moment";
import { IconBrandYoutube, IconExternalLink } from "@tabler/icons-react";
import { calculatePerformanceMetrics } from "../../utils/youtubeUtils";

interface VideoDetailsCardProps {
  video: youtube_v3.Schema$Video;
}

const VideoDetailsCard: React.FC<VideoDetailsCardProps> = ({ video }) => {
  const metrics = calculatePerformanceMetrics(video);

  return (
    <div className="mb-8">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-1/3">
          {video.snippet?.thumbnails?.high?.url ? (
            <div className="relative">
              <img
                src={video.snippet.thumbnails.high.url}
                alt={video.snippet?.title || "Video thumbnail"}
                className="w-full rounded-lg"
              />
              <a
                href={`https://www.youtube.com/watch?v=${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 flex items-center justify-center hover:bg-black hover:bg-opacity-50 transition-colors rounded-lg"
              >
                <IconBrandYoutube
                  size={48}
                  className="text-red-500 opacity-80 hover:opacity-100"
                />
              </a>
            </div>
          ) : (
            <div className="w-full aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
              <IconBrandYoutube size={48} className="text-red-500" />
            </div>
          )}
        </div>

        <div className="md:w-2/3">
          <h3 className="text-xl font-bold mb-2">{video.snippet?.title}</h3>
          <div className="flex items-center text-gray-400 mb-4">
            <span>{video.snippet?.channelTitle}</span>
            <span className="mx-2">•</span>
            <span>
              {video.snippet?.publishedAt &&
                moment(video.snippet.publishedAt).format("MMM D, YYYY")}
            </span>
          </div>
          <p className="text-gray-300 mb-4 line-clamp-3">
            {video.snippet?.description}
          </p>

          <div className="flex mb-4">
            <a
              href={`https://www.youtube.com/watch?v=${video.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white flex items-center mr-4"
            >
              View on YouTube <IconExternalLink size={14} className="ml-1" />
            </a>
            <a
              href={`https://www.youtube.com/channel/${video.snippet?.channelId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white flex items-center"
            >
              View Channel <IconExternalLink size={14} className="ml-1" />
            </a>
          </div>

          {/* Performance Metrics */}
          {metrics && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="p-3 bg-[#1F1F1F] rounded-lg">
                <p className="text-sm text-gray-400">Views</p>
                <p className="text-xl font-bold">
                  {metrics.views.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-[#1F1F1F] rounded-lg">
                <p className="text-sm text-gray-400">Likes</p>
                <p className="text-xl font-bold">
                  {metrics.likes.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-[#1F1F1F] rounded-lg">
                <p className="text-sm text-gray-400">Comments</p>
                <p className="text-xl font-bold">
                  {metrics.comments.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-[#1F1F1F] rounded-lg">
                <p className="text-sm text-gray-400">Duration</p>
                <p className="text-xl font-bold">{metrics.duration}</p>
              </div>
              <div className="p-3 bg-[#1F1F1F] rounded-lg">
                <p className="text-sm text-gray-400">Engagement Rate</p>
                <p className="text-xl font-bold">{metrics.engagementRate}%</p>
              </div>
              <div className="p-3 bg-[#1F1F1F] rounded-lg">
                <p className="text-sm text-gray-400">Engagement/min</p>
                <p className="text-xl font-bold">
                  {metrics.engagementPerMinute}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoDetailsCard;
